import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSend, MdDownload, MdRefresh, MdZoomIn, MdClose } from 'react-icons/md';
import { helpdeskTicketApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import './HelpdeskUserTicketDetail.css';

function HelpdeskUserTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const loadTicket = useCallback(async () => {
    try {
      const [ticketData, commentsData] = await Promise.all([
        helpdeskTicketApi.getById(ticketId),
        helpdeskTicketApi.getComments(ticketId)
      ]);
      setTicket(ticketData);
      setComments(Array.isArray(commentsData) ? commentsData : []);
    } catch (e) {
      toast.error('Failed to load ticket');
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  // Load photo/attachment previews for image files
  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;

    const loadPhoto = async () => {
      if (ticket.photoName && ticket.photoContentType?.startsWith('image/')) {
        try {
          const response = await helpdeskTicketApi.downloadPhoto(ticket.id);
          if (!cancelled) {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: ticket.photoContentType }));
            setPhotoUrl(url);
          }
        } catch (e) { /* ignore */ }
      }
    };

    const loadAttachmentPreview = async () => {
      if (ticket.attachmentName && ticket.attachmentContentType?.startsWith('image/')) {
        try {
          const response = await helpdeskTicketApi.downloadAttachment(ticket.id);
          if (!cancelled) {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: ticket.attachmentContentType }));
            setAttachmentPreviewUrl(url);
          }
        } catch (e) { /* ignore */ }
      }
    };

    loadPhoto();
    loadAttachmentPreview();

    return () => {
      cancelled = true;
      if (photoUrl) window.URL.revokeObjectURL(photoUrl);
      if (attachmentPreviewUrl) window.URL.revokeObjectURL(attachmentPreviewUrl);
    };
  }, [ticket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await helpdeskTicketApi.addComment(ticketId, { commentText: newComment, internal: false });
      toast.success('Comment added');
      setNewComment('');
      loadTicket();
    } catch (e) {
      toast.error('Failed to add comment');
    }
  };

  const handleReopen = async () => {
    try {
      await helpdeskTicketApi.reopenTicket(ticketId);
      toast.success('Ticket reopened');
      loadTicket();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reopen ticket');
    }
  };

  const handleDownload = async (type) => {
    try {
      const response = type === 'attachment'
        ? await helpdeskTicketApi.downloadAttachment(ticketId)
        : await helpdeskTicketApi.downloadPhoto(ticketId);

      let filename = type === 'attachment' ? ticket?.attachmentName : ticket?.photoName;
      // Sanitize filename to prevent path traversal
      filename = (filename || 'download').replace(/[/\\:*?"<>|\r\n]/g, '_').replace(/^\.+/, '').substring(0, 255);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Failed to download ${type}`);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
      year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: '#2196F3', ASSIGNED: '#FF9800', IN_PROGRESS: '#9C27B0',
      RESOLVED: '#4CAF50', CLOSED: '#607D8B', REOPENED: '#F44336'
    };
    return colors[status] || '#666';
  };

  if (isLoading) return <div className="helpdesk-user-detail"><div className="loading">Loading...</div></div>;
  if (!ticket) return <div className="helpdesk-user-detail"><div className="not-found">Ticket not found</div></div>;

  const canReopen = ticket.ticketStatus === 'RESOLVED' || ticket.ticketStatus === 'CLOSED';

  return (
    <div className="helpdesk-user-detail">
      <div className="detail-header">
        <button className="btn btn-back" onClick={() => navigate('/user/helpdesk/my-tickets')}>
          <MdArrowBack /> Back to My Tickets
        </button>
        <div className="ticket-header-info">
          <h2>{ticket.ticketNumber}</h2>
          <span className="status-badge" style={{ background: getStatusColor(ticket.ticketStatus) + '20', color: getStatusColor(ticket.ticketStatus) }}>
            {ticket.ticketStatus?.replace('_', ' ')}
          </span>
          {ticket.requestType === 'ID_CARD_REQUEST' && ticket.idCardStatus && (
            <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>
              {ticket.idCardStatus?.replace('_', ' ')}
            </span>
          )}
          {canReopen && (
            <button className="btn btn-reopen" onClick={handleReopen}>
              <MdRefresh /> Reopen
            </button>
          )}
        </div>
      </div>

      {/* Ticket Info */}
      <div className="detail-card">
        <h3>Ticket Details</h3>
        <div className="info-grid">
          <div className="info-item"><span className="label">Request Type</span><span className="value">{ticket.requestType === 'GENERAL_REQUEST' ? 'General Request' : 'ID Card Request'}</span></div>
          <div className="info-item"><span className="label">Title</span><span className="value">{ticket.title || '-'}</span></div>
          <div className="info-item"><span className="label">Created</span><span className="value">{formatDateTime(ticket.createdAt)}</span></div>
          {ticket.priority && <div className="info-item"><span className="label">Priority</span><span className="value">{ticket.priority}</span></div>}
          {ticket.assignedTo && <div className="info-item"><span className="label">Assigned To</span><span className="value">{ticket.assignedTo.firstName} {ticket.assignedTo.secondName}</span></div>}
          {ticket.category && <div className="info-item"><span className="label">Category</span><span className="value">{ticket.category.categoryName}</span></div>}
          {ticket.subCategory && <div className="info-item"><span className="label">Sub Category</span><span className="value">{ticket.subCategory.subCategoryName}</span></div>}
          {ticket.city && <div className="info-item"><span className="label">City</span><span className="value">{ticket.city}</span></div>}
          {ticket.workLocation && <div className="info-item"><span className="label">Work Location</span><span className="value">{ticket.workLocation.locationName}</span></div>}
          {ticket.floor && <div className="info-item"><span className="label">Floor</span><span className="value">{ticket.floor}</span></div>}
          {ticket.description && <div className="info-item full-width"><span className="label">Description</span><span className="value">{ticket.description}</span></div>}

          {/* ID Card fields */}
          {ticket.requestType === 'ID_CARD_REQUEST' && (
            <>
              {ticket.employeeId && <div className="info-item"><span className="label">Employee ID</span><span className="value">{ticket.employeeId}</span></div>}
              {ticket.employeeName && <div className="info-item"><span className="label">Employee Name</span><span className="value">{ticket.employeeName}</span></div>}
              {ticket.bloodGroup && <div className="info-item"><span className="label">Blood Group</span><span className="value">{ticket.bloodGroup}</span></div>}
              {ticket.residentialAddress && <div className="info-item full-width"><span className="label">Residential Address</span><span className="value">{ticket.residentialAddress}</span></div>}
            </>
          )}
        </div>

        {/* File Previews & Downloads */}
        {(ticket.attachmentName || ticket.photoName) && (
          <div className="file-section">
            {ticket.photoName && (
              <div className="file-preview-block">
                <span className="file-label">Photo</span>
                {photoUrl ? (
                  <div className="image-preview-wrapper" onClick={() => setLightboxUrl(photoUrl)}>
                    <img src={photoUrl} alt="Employee Photo" className="inline-image-preview" />
                    <div className="image-zoom-overlay"><MdZoomIn /></div>
                  </div>
                ) : (
                  <div className="image-loading">Loading photo...</div>
                )}
                <button className="btn btn-download" onClick={() => handleDownload('photo')}>
                  <MdDownload /> {ticket.photoName}
                </button>
              </div>
            )}
            {ticket.attachmentName && (
              <div className="file-preview-block">
                <span className="file-label">Attachment</span>
                {attachmentPreviewUrl && (
                  <div className="image-preview-wrapper" onClick={() => setLightboxUrl(attachmentPreviewUrl)}>
                    <img src={attachmentPreviewUrl} alt="Attachment" className="inline-image-preview" />
                    <div className="image-zoom-overlay"><MdZoomIn /></div>
                  </div>
                )}
                <button className="btn btn-download" onClick={() => handleDownload('attachment')}>
                  <MdDownload /> {ticket.attachmentName}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="detail-card">
        <h3>Comments</h3>
        <div className="comments-list">
          {comments.length === 0 ? (
            <div className="no-comments">No comments yet</div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="comment-item">
                <div className="comment-header">
                  <strong>{comment.commenterName}</strong>
                  <span className="comment-date">{formatDateTime(comment.createdAt)}</span>
                </div>
                <div className="comment-text">{comment.commentText}</div>
              </div>
            ))
          )}
        </div>

        <div className="add-comment">
          <textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            rows={3}
          />
          <button className="btn btn-primary" onClick={handleAddComment} disabled={!newComment.trim()}>
            <MdSend /> Add Comment
          </button>
        </div>
      </div>
      {/* Lightbox */}
      {lightboxUrl && (
        <div className="image-lightbox" onClick={() => setLightboxUrl(null)}>
          <button className="lightbox-close" onClick={() => setLightboxUrl(null)}><MdClose /></button>
          <img src={lightboxUrl} alt="Full size" className="lightbox-image" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default HelpdeskUserTicketDetail;
